import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  makeStyles,
  tokens,
  Title2,
  Text,
} from '@fluentui/react-components'
import { Dismiss24Regular, ErrorCircle24Regular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  surface: {
    minWidth: '400px',
    maxWidth: '600px',
    borderTop: `4px solid ${tokens.colorBrandForeground1}`,
  },
  surfaceError: {
    minWidth: '400px',
    maxWidth: '600px',
    backgroundColor: '#fdf3f4',
    borderTop: '4px solid #d13438',
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '8px',
    // borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  titleText: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
  },
  content: {
    paddingTop: '8px',
    paddingBottom: '24px',
  },
  actions: {
    marginTop: '24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  errorMessage: {
    marginTop: '16px',
    marginBottom: '16px',
    color: '#c50f1f',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#fde7e9',
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid #fec7ca',
  },
  spinner: {
    marginRight: '8px',
  },
})

interface ActionConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
  error: Error | null
  onReset: () => void
  title: string
  message: string
  confirmLabel: string
}

export const ActionConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  error,
  onReset,
  title,
  message,
  confirmLabel,
}: ActionConfirmationModalProps) => {
  const styles = useStyles()

  const handleClose = () => {
    if (!isLoading) {
      onReset()
      onClose()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => !data.open && handleClose()}
    >
      <DialogSurface className={error ? styles.surfaceError : styles.surface}>
        <DialogBody>
          <DialogTitle className={styles.title}>
            <Title2 className={styles.titleText}>
              {error ? `Fel vid ${title.toLowerCase()}` : title}
            </Title2>
            <Button
              appearance="transparent"
              icon={<Dismiss24Regular />}
              onClick={handleClose}
              disabled={isLoading}
              aria-label="Stäng"
            />
          </DialogTitle>
          <DialogContent className={styles.content}>
            {!error ? (
              <Text>{message}</Text>
            ) : (
              <div className={styles.errorMessage}>
                <ErrorCircle24Regular />
                <span>Ett fel uppstod: {error.message}</span>
              </div>
            )}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button
              appearance="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Avbryt
            </Button>
            <Button
              appearance="primary"
              onClick={error ? onConfirm : onConfirm} // Retry logic is same as confirm
              disabled={isLoading}
            >
              {isLoading && <Spinner size="tiny" className={styles.spinner} />}
              {error ? 'Försök igen' : confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
